import type { TFunction } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageSourcePropType, StyleSheet, Text, View } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { useSelector } from 'react-redux';

import { IconCloseSvg } from '../../../assets/inventory';
import ChipsGainedPng from '../../../assets/profileRedesign/chips-gained.png';
import LossesPng from '../../../assets/profileRedesign/losses.png';
import WinsPng from '../../../assets/profileRedesign/wins.png';
import AverageBetIcon from '../../../assets/profileStatistics/average-bet.svg';
import BackIcon from '../../../assets/profileStatistics/back.svg';
import BestFinishIcon from '../../../assets/profileStatistics/best-finish.svg';
import BiggestWinIcon from '../../../assets/profileStatistics/biggest-win.svg';
import ChipsWageredIcon from '../../../assets/profileStatistics/chips-wagered.svg';
import GameStatsIcon from '../../../assets/profileStatistics/game-stats.svg';
import GamesPlayedIcon from '../../../assets/profileStatistics/games-played.svg';
import LastVisitIcon from '../../../assets/profileStatistics/last-visit.svg';
import League1Png from '../../../assets/profileStatistics/league-1.png';
import League2Png from '../../../assets/profileStatistics/league-2.png';
import League3Png from '../../../assets/profileStatistics/league-3.png';
import MemberSinceIcon from '../../../assets/profileStatistics/member-since.svg';
import SummaryIcon from '../../../assets/profileStatistics/summary.svg';
import TimePlayedIcon from '../../../assets/profileStatistics/time-played.svg';
import TotalTournamentsIcon from '../../../assets/profileStatistics/total-tournaments.svg';
import TournamentWinRateIcon from '../../../assets/profileStatistics/tournament-win-rate.svg';
import TournamentWinningsIcon from '../../../assets/profileStatistics/tournament-winnings.svg';
import TrophyBronzeIcon from '../../../assets/profileStatistics/trophy-bronze.svg';
import TrophyGoldIcon from '../../../assets/profileStatistics/trophy-gold.svg';
import TrophySilverIcon from '../../../assets/profileStatistics/trophy-silver.svg';
import WinRateIcon from '../../../assets/profileStatistics/win-rate.svg';
import WinStreakIcon from '../../../assets/profileStatistics/win-streak.svg';
import {
	getMyTournamentStatistics,
	getPlayerTournamentStatistics,
	MyTournamentStatistics,
	TournamentStatisticsBreakdown,
} from '../../../services/api';
import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { timeAgo } from '../../../utils/helpers/date';
import { FormatWithThousandSeparator } from '../../../utils/helpers/numberFormat';
import { secondsToDHM } from '../../../utils/helpers/time';
import { useDevice } from '../../../utils/hooks/useDevice';
import { ACHIEVEMENTS_FONT, achievementsColors } from '../Achievements/achievementsTheme';
import { GradientFill } from '../common/GradientFill';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import RedesignModal from '../Modals/RedesignModal';
import {
	DESKTOP_TWIN_FACTOR,
	DESKTOP_TWIN_SCALE,
	useRedesignScale,
} from '../Modals/useRedesignScale';

/**
 * Figma "Statistics / Mobile_bogi" (4323:42129), section "Statistics" on page "BOGI DRAFT" - what
 * the profile's statistics chevron opens. It is drawn in the same 358-wide panel as the profile,
 * so like the edit view it swaps in for the profile body rather than stacking a second modal, and
 * its back control hands the profile back.
 *
 * The figures come from the same `/api/player/statistics` payload the profile already loaded, and
 * the tournament columns from the tournament statistics endpoint the old profile used. Whatever
 * the file draws that neither payload carries yet shows a dash rather than a made-up number.
 */
type Props = {
	onBack: () => void;
	onClose: () => void;
	renderInline?: boolean;
	/** The profile being viewed when it is not the signed-in player's own. */
	playerId?: string;
};

type SvgIcon = React.FC<SvgProps>;

// "Header bar" (4323:42131): a 334x44 row at 12/12 - a 44 back control, the title and a 44 close.
const HEADER = { inset: 12, height: 44, gap: 8, button: 44, icon: 24 };
// The sections are 334 wide at x=12; the 4-wide scroll track sits in the 12 to their right.
const BODY = { paddingX: 12, paddingTop: 12, paddingBottom: 20, gap: 12 };
const CARD = { radius: 12, padding: 12, border: '#DA600E' };
const MISSING = '-';

/**
 * Figma "Statistics / Desktop — Full content" (5605:101689), page "PAVEL DRAFT": the same sections
 * in an 860x964.5 modal 30 down a 1440x1024 frame, split into two 402.5 columns - hero, account and
 * game statistics on the left, the two tournament cards on the right - drawn with the 1.25x
 * desktop twins. Divided back by 1.25 that is a 688-wide panel with 322-wide columns, so the
 * mobile sections drop into the columns as they are; only the summary plates stretch, so both
 * columns end level the way the frame draws them.
 */
const DESKTOP = {
	width: 860 / DESKTOP_TWIN_SCALE,
	height: 964.5 / DESKTOP_TWIN_SCALE,
	/** "Header bar" and "Columns" sit 20 in from the panel's sides. */
	paddingX: 20 / DESKTOP_TWIN_SCALE,
	/** The columns stop 20 short of the panel's bottom. */
	paddingBottom: 20 / DESKTOP_TWIN_SCALE,
	columnGap: 15 / DESKTOP_TWIN_SCALE,
	/** The panel's 30 from the top of the 1440 frame, in the 1280 frame's units. */
	margin: 30 / 1.125,
} as const;

/** The file's own variables the profile theme does not already name. */
const colors = {
	/** border/subtle - the hairline between rows. */
	divider: '#5C2416',
	/** border/gold - the summary plates. */
	borderGold: '#E89200',
	/** surface/base - a medal count's plate. */
	medalPlate: '#1A0603',
	medalBorder: '#FCF17B',
	lossesValue: '#35C3F3',
	chipsValue: '#0CEB66',
} as const;

/**
 * "Tier / League 1-3" (4111:29376 / 4111:29520 / 4111:30902). The tournament API names the
 * leagues bronze, silver and gold, in the same order the file draws them.
 */
const LEAGUES: Array<{
	key: keyof MyTournamentStatistics;
	artwork: ImageSourcePropType;
	artworkSize: { width: number; height: number };
	border: string;
	fill: Array<[string, string]>;
	totalColor: string;
}> = [
	{
		key: 'bronze',
		artwork: League1Png,
		artworkSize: { width: 124, height: 124 },
		border: '#FF3B2F',
		fill: [
			['0', '#5A0A0A'],
			['1', '#2A0505'],
		],
		totalColor: achievementsColors.gold200,
	},
	{
		key: 'silver',
		artwork: League2Png,
		artworkSize: { width: 118, height: 119 },
		border: '#3B7FD4',
		fill: [
			['0', '#0B2A5A'],
			['1', '#061428'],
		],
		totalColor: colors.lossesValue,
	},
	{
		key: 'gold',
		artwork: League3Png,
		artworkSize: { width: 125, height: 125 },
		border: '#E89200',
		fill: [
			['0', '#5A3A08'],
			['1', '#2A1A02'],
		],
		totalColor: achievementsColors.gold200,
	},
];

/** "Tournament / Medal Count" (4110:29367): 1st, 2nd and 3rd place, gold to bronze. */
const MEDALS: Array<{ place: '1' | '2' | '3'; Icon: SvgIcon }> = [
	{ place: '1', Icon: TrophyGoldIcon },
	{ place: '2', Icon: TrophySilverIcon },
	{ place: '3', Icon: TrophyBronzeIcon },
];

/** Translation key and English default for each month, January first. */
const MONTHS: Array<[string, string]> = [
	['T_SITE_PROFILE_MONTH_JAN', 'Jan'],
	['T_SITE_PROFILE_MONTH_FEB', 'Feb'],
	['T_SITE_PROFILE_MONTH_MAR', 'Mar'],
	['T_SITE_PROFILE_MONTH_APR', 'Apr'],
	['T_SITE_PROFILE_MONTH_MAY', 'May'],
	['T_SITE_PROFILE_MONTH_JUN', 'Jun'],
	['T_SITE_PROFILE_MONTH_JUL', 'Jul'],
	['T_SITE_PROFILE_MONTH_AUG', 'Aug'],
	['T_SITE_PROFILE_MONTH_SEP', 'Sep'],
	['T_SITE_PROFILE_MONTH_OCT', 'Oct'],
	['T_SITE_PROFILE_MONTH_NOV', 'Nov'],
	['T_SITE_PROFILE_MONTH_DEC', 'Dec'],
];
const ORDINALS: Record<string, [string, string]> = {
	'1': ['T_SITE_PROFILE_ORDINAL_1', 'st'],
	'2': ['T_SITE_PROFILE_ORDINAL_2', 'nd'],
	'3': ['T_SITE_PROFILE_ORDINAL_3', 'rd'],
};

/** "25 Aug 2026" - spelled out by hand so it does not depend on the engine's Intl data. */
const formatMemberSince = (t: TFunction, value?: string | null) => {
	if (!value) return MISSING;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return MISSING;
	const [monthKey, monthName] = MONTHS[date.getMonth()];
	return `${date.getDate()} ${t(monthKey, { defaultValue: monthName })} ${date.getFullYear()}`;
};

const formatTimePlayed = (t: TFunction, seconds?: number | string) => {
	if (seconds === undefined || seconds === null) return MISSING;
	const { days, hours, minutes } = secondsToDHM(seconds);
	return t('T_SITE_PROFILE_TIME_PLAYED', {
		defaultValue: '{{days}}d {{hours}}h {{minutes}}m',
		days,
		hours,
		minutes,
	});
};

const formatCount = (value?: number | string | null) => {
	if (value === undefined || value === null || value === '') return MISSING;
	const number = Number(value);
	return Number.isFinite(number)
		? FormatWithThousandSeparator(number, { fractionDigits: 0 })
		: MISSING;
};

/** A section heading the file lays out by hand: an icon and a centred title at fixed offsets. */
type HeadingLayout = {
	height: number;
	paddingBottom?: number;
	icon: { left: number; top: number; width: number; height: number };
	title: { left: number; top: number; width: number };
};

const ProfileRedesignStatisticsView = ({
	onBack,
	onClose,
	renderInline = false,
	playerId,
}: Props) => {
	const { t } = useTranslation();
	const { isDesktop } = useDevice();
	const scale = useRedesignScale(
		isDesktop
			? {
					fitHeight: DESKTOP.height,
					panelWidth: DESKTOP.width,
					desktopFactor: DESKTOP_TWIN_FACTOR,
					desktopMargin: DESKTOP.margin,
					minFitScale: 0.9,
				}
			: undefined,
	);
	const styles = useMemo(() => createStyles(scale, isDesktop), [scale, isDesktop]);
	const s = (value: number) => value * scale;

	const isLogged = useSelector(selectIsLogged);
	const stats = useSelector((state: RootState) => state.auth.statistics);
	const [tournaments, setTournaments] = useState<MyTournamentStatistics | null>(null);

	useEffect(() => {
		if (!isLogged) return;
		let mounted = true;

		(playerId ? getPlayerTournamentStatistics(playerId) : getMyTournamentStatistics())
			.then(result => {
				if (mounted) setTournaments(result);
			})
			.catch(() => {
				if (mounted) setTournaments(null);
			});

		return () => {
			mounted = false;
		};
	}, [isLogged, playerId]);

	const games = stats?.gamesData;
	const wins = games ? Number(games.svarka_wins) || 0 : undefined;
	const losses = games ? Number(games.svarka_loses) || 0 : undefined;
	const gamesPlayed = wins !== undefined && losses !== undefined ? wins + losses : undefined;
	const winRate =
		gamesPlayed && wins !== undefined ? `${((wins / gamesPlayed) * 100).toFixed(1)}%` : MISSING;

	const bestPlace = tournaments
		? MEDALS.find(({ place }) =>
				LEAGUES.some(league => (tournaments[league.key]?.[place] ?? 0) > 0),
			)?.place
		: undefined;

	const heroStats = [
		{
			label: t('T_SITE_PROFILE_WINS', { defaultValue: 'Wins' }).toUpperCase(),
			value: wins,
			artwork: WinsPng,
			color: achievementsColors.accent,
		},
		{
			label: t('T_SITE_PROFILE_LOSSES', { defaultValue: 'Losses' }).toUpperCase(),
			value: losses,
			artwork: LossesPng,
			color: colors.lossesValue,
		},
		{
			label: t('T_SITE_PROFILE_CHIUPS_GAINED', { defaultValue: 'Chips gained' }).toUpperCase(),
			value: games?.svarka_chips_wins,
			artwork: ChipsGainedPng,
			color: colors.chipsValue,
		},
	];

	const accountRows: Array<{
		label: string;
		value: string;
		Icon: SvgIcon;
		size: [number, number];
	}> = [
		{
			label: t('T_SITE_PROFILE_MEMBER_SINCE', { defaultValue: 'Member since' }),
			value: formatMemberSince(t, stats?.player.registrationDate),
			Icon: MemberSinceIcon,
			size: [25, 25],
		},
		{
			label: t('T_SITE_PROFILE_LAST_VISIT', { defaultValue: 'Last visit' }),
			value: stats?.player.lastLoginDate ? timeAgo(stats.player.lastLoginDate) : MISSING,
			Icon: LastVisitIcon,
			size: [28, 28],
		},
		{
			label: t('T_SITE_PROFILE_TOTAL_TIME', { defaultValue: 'Total time in play' }),
			value: formatTimePlayed(t, games?.svarka_time_played),
			Icon: TimePlayedIcon,
			size: [33, 19],
		},
	];

	// The API does not report wagers, bets, biggest wins or streaks yet.
	const gameRows: Array<{
		label: string;
		value: string;
		Icon: SvgIcon;
		size: [number, number];
		mirrored?: boolean;
	}> = [
		{
			label: t('T_SITE_PROFILE_TOTAL_GAMES', { defaultValue: 'Total Games Played' }),
			value: formatCount(gamesPlayed),
			Icon: GamesPlayedIcon,
			size: [28, 23],
		},
		{
			label: t('T_SITE_PROFILE_TOTAL_WAGERED', { defaultValue: 'Total Chips Wagered' }),
			value: MISSING,
			Icon: ChipsWageredIcon,
			size: [28, 29],
		},
		{
			label: t('T_SITE_PROFILE_WIN_RATE', { defaultValue: 'Win Rate' }),
			value: winRate,
			Icon: WinRateIcon,
			size: [30, 30],
		},
		// The file flips this coin stack horizontally (4323:42479).
		{
			label: t('T_SITE_PROFILE_AVERAGE_BET', { defaultValue: 'Average Bet' }),
			value: MISSING,
			Icon: AverageBetIcon,
			size: [28, 28],
			mirrored: true,
		},
		{
			label: t('T_SITE_PROFILE_BIGGEST_WIN', { defaultValue: 'Biggest Win' }),
			value: MISSING,
			Icon: BiggestWinIcon,
			size: [25, 23],
		},
		{
			label: t('T_SITE_PROFILE_LONGEST_STREAK', { defaultValue: 'Longest Win Streak' }),
			value: MISSING,
			Icon: WinStreakIcon,
			size: [29, 35],
		},
	];

	const renderHeading = (title: string, Icon: SvgIcon, layout: HeadingLayout) => (
		<View
			style={{
				width: '100%',
				height: s(layout.height),
				paddingBottom: layout.paddingBottom ? s(layout.paddingBottom) : 0,
			}}
		>
			<View
				style={{
					position: 'absolute',
					left: s(layout.icon.left),
					top: s(layout.icon.top),
				}}
			>
				<Icon width={s(layout.icon.width)} height={s(layout.icon.height)} />
			</View>
			<Text
				numberOfLines={1}
				style={[
					styles.sectionTitle,
					{
						position: 'absolute',
						left: s(layout.title.left),
						top: s(layout.title.top),
						width: s(layout.title.width),
					},
				]}
			>
				{title}
			</Text>
		</View>
	);

	const renderLeague = (league: (typeof LEAGUES)[number]) => {
		const breakdown: TournamentStatisticsBreakdown | undefined = tournaments?.[league.key];

		return (
			<View key={league.key} style={[styles.tier, { borderColor: league.border }]}>
				{/* Its own full-size layer: an SVG's 100% resolves against the card's padded box. */}
				<View pointerEvents='none' style={StyleSheet.absoluteFill}>
					<GradientFill stops={league.fill} />
				</View>
				<View style={styles.tierArtwork}>
					<Image
						source={league.artwork}
						resizeMode='cover'
						style={{
							width: s(league.artworkSize.width),
							height: s(league.artworkSize.height),
						}}
					/>
				</View>
				{MEDALS.map(({ place, Icon }) => (
					<View key={place} style={styles.medal}>
						<View>
							<Icon width={s(22)} height={s(20)} />
						</View>
						<Text numberOfLines={1} style={styles.medalCount}>
							{breakdown ? formatCount(breakdown[place]) : MISSING}
						</Text>
					</View>
				))}
				<Text numberOfLines={1} style={[styles.tierTotal, { color: league.totalColor }]}>
					{t('T_SITE_PROFILE_TOURNAMENT_WINNINGS_TOTAL', { defaultValue: 'total:' }).toUpperCase()}{' '}
					{breakdown ? formatCount(breakdown.total) : MISSING}
				</Text>
			</View>
		);
	};

	// "Hero statistics" (4323:42139)
	const heroSection = (
		<View style={styles.hero}>
			{heroStats.map(item => (
				<View key={item.label} style={[styles.card, styles.heroCard]}>
					<Image source={item.artwork} resizeMode='contain' style={styles.heroArtwork} />
					<Text numberOfLines={1} style={[styles.heroValue, { color: item.color }]}>
						{formatCount(item.value)}
					</Text>
					<Text numberOfLines={1} style={styles.overline}>
						{item.label}
					</Text>
				</View>
			))}
		</View>
	);
	// "Account info" (4323:42155)
	const accountSection = (
		<View style={[styles.card, styles.listCard]}>
			{accountRows.map((row, index) => (
				<React.Fragment key={row.label}>
					{index > 0 ? <View style={styles.divider} /> : null}
					<View style={styles.infoRow}>
						<View>
							<row.Icon width={s(row.size[0])} height={s(row.size[1])} />
						</View>
						<View style={styles.rowText}>
							<Text numberOfLines={1} style={styles.muted}>
								{row.label}
							</Text>
							<Text numberOfLines={1} style={styles.value}>
								{row.value}
							</Text>
						</View>
					</View>
				</React.Fragment>
			))}
		</View>
	);
	// "Game Statistics" (4323:42268)
	const gameSection = (
		<View style={[styles.card, styles.listCard]}>
			<View style={styles.divider} />
			{renderHeading(
				t('T_SITE_PROFILE_GAME_STATISTICS', { defaultValue: 'Game Statistics' }),
				GameStatsIcon,
				{
					height: 36,
					paddingBottom: 4,
					icon: { left: 67, top: 0, width: 26, height: 24 },
					title: { left: 41, top: 4, width: 249 },
				},
			)}
			{gameRows.map(row => (
				<React.Fragment key={row.label}>
					<View style={styles.divider} />
					<View style={styles.statRow}>
						<View style={row.mirrored ? styles.mirrored : undefined}>
							<row.Icon width={s(row.size[0])} height={s(row.size[1])} />
						</View>
						<View style={styles.rowText}>
							<Text numberOfLines={1} style={styles.value}>
								{row.value}
							</Text>
							<Text numberOfLines={1} style={styles.muted}>
								{row.label}
							</Text>
						</View>
					</View>
				</React.Fragment>
			))}
		</View>
	);
	// "Tournament Winnings" (4323:42539)
	const winningsSection = (
		<View style={[styles.card, styles.paddedCard]}>
			{renderHeading(
				t('T_SITE_PROFILE_TOURNAMENT_WINNINGS_TITLE', { defaultValue: 'Tournament Winnings' }),
				TournamentWinningsIcon,
				{
					height: 28,
					icon: { left: 38, top: 0, width: 31, height: 28 },
					title: { left: 64, top: 3, width: 193 },
				},
			)}
			<View style={styles.tiers}>{LEAGUES.map(renderLeague)}</View>
		</View>
	);
	// "Tournament Summary" (4323:42579)
	const summarySection = (
		<View style={[styles.card, styles.paddedCard, styles.summaryCard]}>
			{renderHeading(
				t('T_SITE_PROFILE_TOURNAMENT_SUMMARY', { defaultValue: 'Tournament Summary' }),
				SummaryIcon,
				{
					height: 30,
					paddingBottom: 4,
					icon: { left: 36, top: 0, width: 28, height: 26 },
					title: { left: 21, top: 1.5, width: 276 },
				},
			)}

			<View style={[styles.summaryPlate, styles.bestFinish]}>
				<View>
					<BestFinishIcon width={s(31.5)} height={s(42)} />
				</View>
				<View style={styles.bestFinishText}>
					<Text numberOfLines={1} style={[styles.muted, styles.centered]}>
						{t('T_SITE_PROFILE_BEST_TOURNAMENT_FINISH', { defaultValue: 'Best Tournament Finish' })}
					</Text>
					{bestPlace ? (
						<View style={styles.place}>
							<Text style={styles.placeText}>{bestPlace}</Text>
							<Text style={[styles.placeText, styles.placeOrdinal]}>
								{t(ORDINALS[bestPlace][0], { defaultValue: ORDINALS[bestPlace][1] })}
							</Text>
							<Text style={styles.placeText}>
								{' '}
								{t('T_SITE_PROFILE_PLACE', { defaultValue: 'Place' })}
							</Text>
						</View>
					) : (
						<Text style={[styles.placeText, styles.centered]}>{MISSING}</Text>
					)}
				</View>
			</View>

			{/* The API does not report how many tournaments were played yet. */}
			<View style={styles.metrics}>
				<View style={[styles.summaryPlate, styles.metric]}>
					<View>
						<TotalTournamentsIcon width={s(27)} height={s(21)} />
					</View>
					<Text numberOfLines={1} style={[styles.muted, styles.centered]}>
						{t('T_SITE_PROFILE_TOTAL_TOURNAMENTS', { defaultValue: 'Total Tournaments' })}
					</Text>
					<Text numberOfLines={1} style={styles.metricValue}>
						{MISSING}
					</Text>
				</View>
				<View style={[styles.summaryPlate, styles.metric]}>
					<View style={styles.metricIconBox}>
						<TournamentWinRateIcon width={s(27)} height={s(27)} />
					</View>
					<Text numberOfLines={1} style={[styles.muted, styles.centered]}>
						{t('T_SITE_PROFILE_TOURNAMENT_WIN_RATE', { defaultValue: 'Tournament Win Rate' })}
					</Text>
					<Text numberOfLines={1} style={styles.metricValue}>
						{MISSING}
					</Text>
				</View>
			</View>
		</View>
	);

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			renderInline={renderInline}
			desktopTwin
			panel={isDesktop ? { height: DESKTOP.height, width: DESKTOP.width } : undefined}
		>
			<View style={styles.header}>
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_BACK_TO_PROFILE', { defaultValue: 'Back to profile' })}
					onPress={onBack}
					style={styles.headerButton}
				>
					{/* Wrapped for the same painting-order reason as the profile's tab icons. */}
					<View>
						<BackIcon width={s(HEADER.icon)} height={s(HEADER.icon)} />
					</View>
				</Pressable>
				<Text numberOfLines={1} style={styles.title}>
					{t('T_SITE_PROFILE_STATISTICS', { defaultValue: 'Statistics' })}
				</Text>
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_CLOSE_STATISTICS', {
						defaultValue: 'Close statistics',
					})}
					onPress={onClose}
					style={styles.headerButton}
				>
					<View>
						<IconCloseSvg width={s(HEADER.icon)} height={s(HEADER.icon)} />
					</View>
				</Pressable>
			</View>

			<RedesignScrollView
				contentContainerStyle={styles.content}
				indicatorInsets={{ right: s(4), top: 0, bottom: s(BODY.paddingBottom) }}
				reserveGutter={false}
			>
				{isDesktop ? (
					<View style={styles.columns}>
						<View style={styles.column}>
							{heroSection}
							{accountSection}
							{gameSection}
						</View>
						<View style={styles.column}>
							{winningsSection}
							{summarySection}
						</View>
					</View>
				) : (
					<>
						{heroSection}
						{accountSection}
						{gameSection}
						{winningsSection}
						{summarySection}
					</>
				)}
			</RedesignScrollView>
		</RedesignModal>
	);
};

const createStyles = (scale: number, isDesktop: boolean) => {
	const s = (value: number) => value * scale;
	const hairline = Math.max(1, s(1));

	return StyleSheet.create({
		header: {
			height: s(HEADER.height),
			marginTop: s(HEADER.inset),
			marginHorizontal: s(isDesktop ? DESKTOP.paddingX : HEADER.inset),
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(HEADER.gap),
		},
		headerButton: {
			width: s(HEADER.button),
			height: s(HEADER.button),
			alignItems: 'center',
			justifyContent: 'center',
		},
		// "DS/Heading/L": Sofia Sans Bold 24, line-height 1.3, text/accent.
		title: {
			flex: 1,
			color: achievementsColors.accent,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(24),
			fontWeight: '700',
			lineHeight: s(24 * 1.3),
			textAlign: 'center',
		},
		content: {
			paddingTop: s(BODY.paddingTop),
			paddingHorizontal: s(isDesktop ? DESKTOP.paddingX : BODY.paddingX),
			paddingBottom: s(isDesktop ? DESKTOP.paddingBottom : BODY.paddingBottom),
			rowGap: s(BODY.gap),
		},
		// Desktop: two equal columns that stretch to the taller one, so both end level.
		columns: {
			flexDirection: 'row',
			columnGap: s(DESKTOP.columnGap),
		},
		column: {
			flex: 1,
			rowGap: s(BODY.gap),
		},
		// Every section is the file's surface/raised plate behind the orange hairline.
		card: {
			borderRadius: s(CARD.radius),
			borderWidth: hairline,
			borderColor: CARD.border,
			backgroundColor: achievementsColors.surface,
		},
		listCard: {
			paddingHorizontal: s(CARD.padding),
			paddingVertical: s(4),
		},
		paddedCard: {
			padding: s(CARD.padding),
			rowGap: s(12),
		},
		summaryCard: {
			rowGap: s(8),
			// The desktop frame stretches the summary down to the left column's last card.
			...(isDesktop ? { flex: 1 } : null),
		},
		divider: {
			height: 1,
			backgroundColor: colors.divider,
		},
		hero: {
			flexDirection: 'row',
			columnGap: s(8),
		},
		heroCard: {
			flex: 1,
			alignItems: 'center',
			rowGap: s(8),
			paddingHorizontal: s(8),
			paddingVertical: s(12),
		},
		heroArtwork: {
			width: s(60),
			height: s(59),
		},
		// "DS/Numeric/M": Sofia Sans Bold 16, line-height 1, letter-spacing -1%.
		heroValue: {
			alignSelf: 'stretch',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(16),
			fontWeight: '700',
			lineHeight: s(16),
			letterSpacing: s(-0.16),
			textAlign: 'center',
		},
		// "DS/Overline/S": Sofia Sans Black 10, line-height 1.2, letter-spacing 6%, gold/50.
		overline: {
			alignSelf: 'stretch',
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(10),
			fontWeight: '900',
			lineHeight: s(12),
			letterSpacing: s(0.6),
			textAlign: 'center',
		},
		infoRow: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(12),
			paddingVertical: s(10),
		},
		statRow: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(12),
			paddingHorizontal: s(12),
			paddingVertical: s(8),
		},
		mirrored: {
			transform: [{ scaleX: -1 }],
		},
		rowText: {
			flex: 1,
			rowGap: s(2),
		},
		// "DS/Body/S": Sofia Sans Medium 12, line-height 1.4, text/muted.
		muted: {
			color: achievementsColors.muted,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(12),
			fontWeight: '500',
			lineHeight: s(12 * 1.4),
		},
		value: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(16),
			fontWeight: '700',
			lineHeight: s(16),
			letterSpacing: s(-0.16),
		},
		// "DS/Heading/M": Sofia Sans Bold 18, line-height 1.3, gold/50.
		sectionTitle: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(18),
			fontWeight: '700',
			lineHeight: s(18 * 1.3),
			textAlign: 'center',
		},
		tiers: {
			flexDirection: 'row',
			columnGap: s(8),
		},
		// The artwork is wider than the card; the file clips it, glow and all, to the card.
		tier: {
			flex: 1,
			height: s(266),
			alignItems: 'center',
			rowGap: s(6),
			padding: s(8),
			borderRadius: s(8),
			borderWidth: hairline,
			overflow: 'hidden',
		},
		tierArtwork: {
			height: s(124),
			alignItems: 'center',
			justifyContent: 'center',
		},
		medal: {
			alignSelf: 'stretch',
			height: s(30),
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(6),
			paddingHorizontal: s(8),
			borderRadius: s(4),
			borderWidth: Math.max(StyleSheet.hairlineWidth, s(0.5)),
			borderColor: colors.medalBorder,
			backgroundColor: colors.medalPlate,
		},
		// "DS/Numeric/S": Sofia Sans Bold 14, line-height 1, letter-spacing -1%, text/primary.
		medalCount: {
			flex: 1,
			color: achievementsColors.primary,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(14),
			fontWeight: '700',
			lineHeight: s(14),
			letterSpacing: s(-0.14),
		},
		tierTotal: {
			alignSelf: 'stretch',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(10),
			fontWeight: '900',
			lineHeight: s(12),
			letterSpacing: s(0.6),
		},
		summaryPlate: {
			borderRadius: s(8),
			borderWidth: hairline,
			borderColor: colors.borderGold,
			backgroundColor: achievementsColors.surface,
			paddingHorizontal: s(12),
			paddingVertical: s(10),
		},
		bestFinish: {
			...(isDesktop ? { flex: 1 } : null),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			columnGap: s(12),
		},
		bestFinishText: {
			width: s(123),
			rowGap: s(2),
		},
		centered: {
			textAlign: 'center',
		},
		place: {
			flexDirection: 'row',
			alignItems: 'flex-start',
			justifyContent: 'center',
		},
		// "DS/Numeric/L": Sofia Sans Bold 24, line-height 1, letter-spacing -1%, text/accent.
		placeText: {
			color: achievementsColors.accent,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(24),
			fontWeight: '700',
			lineHeight: s(24),
			letterSpacing: s(-0.24),
		},
		placeOrdinal: {
			fontSize: s(15.48),
			lineHeight: s(15.48),
		},
		metrics: {
			...(isDesktop ? { flex: 1 } : null),
			flexDirection: 'row',
			columnGap: s(8),
		},
		metric: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
			rowGap: s(6),
		},
		metricIconBox: {
			width: s(22),
			height: s(22),
			alignItems: 'center',
			justifyContent: 'center',
		},
		metricValue: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(24),
			fontWeight: '700',
			lineHeight: s(24),
			letterSpacing: s(-0.24),
			textAlign: 'center',
		},
	});
};

export default ProfileRedesignStatisticsView;
